import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { addDays, format, parseISO, startOfToday } from "date-fns";

const Ctx = createContext(null);
const KEY = "eres-booking";
const iso = (d) => format(d, "yyyy-MM-dd");

const defaults = () => ({
  criteria: {
    country_id: 0, city_id: 0, hotel_id: "", hotel_name: "",
    arrival: iso(addDays(startOfToday(), 14)), nights: 2,
    rooms: 1, adults: 2, kids: 0, promo: "", show_packages: true, packages_only: false,
  },
  hotel: null, room: null, rate: null, services: {},
});

const load = (key) => {
  try {
    const s = JSON.parse(sessionStorage.getItem(key));
    if (!s?.criteria || parseISO(s.criteria.arrival) < startOfToday()) return defaults();
    return s;
  } catch {
    return defaults();
  }
};

const empty = { hotel: null, room: null, rate: null, services: {} };

export const BookingProvider = ({ slug, children }) => {
  const key = `${KEY}-${slug}`;
  const [state, setState] = useState(() => load(key));
  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(state));
  }, [state, key]);

  // Stable action identities so they can safely be used as effect dependencies
  const actions = useMemo(() => ({
    setCriteria: (c) => setState((s) => ({ ...s, ...empty, criteria: { ...s.criteria, ...c } })),
    selectHotel: (hotel) => setState((s) => (s.hotel?.id === hotel.id ? { ...s, hotel } : { ...s, ...empty, hotel })),
    selectRate: (room, rate) => setState((s) => ({ ...s, room, rate })),
    setService: (id, qty) => setState((s) => ({ ...s, services: { ...s.services, [id]: Math.max(0, qty) } })),
    clearItinerary: () => setState((s) => ({ ...s, ...empty })),
  }), []);

  const value = useMemo(() => ({
    ...state,
    departure: iso(addDays(parseISO(state.criteria.arrival), state.criteria.nights)),
    ...actions,
  }), [state, actions]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useBooking = () => useContext(Ctx);
